import React from 'react';
import { mdiCommentOutline } from '@mdi/js';
import { renderDate } from '../../../..';
import { getNickname, isHtmlContent, sanitizeHtml } from '../../../messaging/util';
import * as S from '../claim-timeline.styles';
import * as Card from './timeline-detail-card.styles';
import { ICommentDetailProps } from './comment-detail.types';
import { ITimelineEvent } from '../claim-timeline.types';

/**
 * CommentDetail Component
 *
 * Displays detailed information about a comment including:
 * - Comment ID, Author, Created Date
 * - Body (supports both HTML and Markdown)
 *
 * Used by both Provider and Support apps in their timeline views.
 */
export const CommentDetail: React.FunctionComponent<ICommentDetailProps> = (
    props: ICommentDetailProps,
): React.ReactElement => {
    const { comment } = props;

    const renderCommentBody = (): React.ReactElement => {
        if (!comment.body) {
            return <></>;
        }

        const isHtml = isHtmlContent(comment.body);

        if (isHtml) {
            return <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.body) }} />;
        }

        return <S.MarkdownBlock text={comment.body} />;
    };

    return (
        <Card.CdPanel $embedded key={comment.id}>
            <Card.CdSectionCard $embedded>
                <S.ExpandTable expand={true}>
                    <S.ExpandTBody>
                        <S.ExpandTR>
                            <S.ExpandTH>Comment ID</S.ExpandTH>
                            <S.ExpandTD>{comment.id}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Author</S.ExpandTH>
                            <S.ExpandTD>{getNickname(comment.author)}</S.ExpandTD>
                        </S.ExpandTR>
                        <S.ExpandTR>
                            <S.ExpandTH>Created Date</S.ExpandTH>
                            <S.ExpandTD>{renderDate(comment.created)}</S.ExpandTD>
                        </S.ExpandTR>
                    </S.ExpandTBody>
                </S.ExpandTable>
            </Card.CdSectionCard>
            {comment.body && (
                <Card.CdSectionCard $embedded>
                    <S.ExpandTDCommentBody>{renderCommentBody()}</S.ExpandTDCommentBody>
                </Card.CdSectionCard>
            )}
        </Card.CdPanel>
    );
};

/**
 * Helper function to create a timeline event for a comment
 *
 * @param comment - The comment object (ProviderComment or SupportComment)
 * @returns ITimelineEvent with comment details
 */
export function createCommentTimelineEvent(comment: ICommentDetailProps['comment']): ITimelineEvent {
    return {
        id: comment.id || '',
        created: comment.created || '',
        event: 'Comment',
        icon: mdiCommentOutline,
        description: `Author: ${getNickname(comment.author) || 'Unknown'}`,
        details: <CommentDetail comment={comment} />,
        type: 'Enter Admin',
    };
}
